"use client";

import React from "react";
import type { RefObject } from "react";
import { Turnstile } from "next-turnstile";
import type { TurnstileValidationState } from "@/hooks/useTurnstile";
import { shouldUseLocalSandboxKey } from "@/lib/turnstile-config";

interface TurnstileFieldProps {
  action: string;
  className?: string;
  containerRef?: RefObject<HTMLDivElement | null>;
  id?: string;
  message?: string | null;
  onError: (error?: unknown) => void;
  onExpire: () => void;
  onVerify: (token: string) => void;
  /** Mount the local dummy widget when `next start` has no public site key. */
  preferLocalSandbox?: boolean;
  state?: TurnstileValidationState;
  visible?: boolean;
}

const statusClasses: Record<TurnstileValidationState, string> = {
  idle: "text-slate-400",
  required: "text-amber-300",
  expired: "text-amber-300",
  error: "text-red-400",
  verified: "text-slate-400",
};

export default function TurnstileField({
  action,
  className = "",
  containerRef,
  id,
  message,
  onError,
  onExpire,
  onVerify,
  preferLocalSandbox = false,
  state = "idle",
  visible = true,
}: TurnstileFieldProps) {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : undefined;
  const useSandbox = shouldUseLocalSandboxKey(hostname, preferLocalSandbox);
  const fallbackSiteKey = "1x00000000000000000000AA";
  const siteKey =
    process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ||
    (useSandbox || process.env.NODE_ENV === "test"
      ? fallbackSiteKey
      : undefined);

  if (!siteKey) {
    return null;
  }

  if (!visible && !message) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={`min-w-0 space-y-2 outline-none ${className}`.trim()}
    >
      {visible && (
        <div className="flex justify-center">
          <Turnstile
            action={action}
            appearance="interaction-only"
            id={id}
            onError={onError}
            onExpire={onExpire}
            onVerify={onVerify}
            refreshExpired="auto"
            responseField={false}
            sandbox={useSandbox ? "pass" : false}
            siteKey={siteKey}
            size="flexible"
          />
        </div>
      )}
      {message && (
        <p
          aria-live="polite"
          className={`break-words whitespace-normal text-sm leading-5 ${statusClasses[state]}`}
          role={state === "error" ? "alert" : "status"}
        >
          {message}
        </p>
      )}
    </div>
  );
}
